'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, LogOut, RefreshCcw, X } from 'lucide-react'

import type { Locale } from '@helper/utils/types'
import { Badge, Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../context'

interface ActiveDevicesListProps {
  className?: string
  locale?: Locale
}

const DEVICE_LABEL = {
  mobile: 'Mobile',
  tablet: 'Tablet',
  desktop: 'Desktop',
} as const

function DeviceRowSkeleton() {
  return (
    <div className='srd:bg-grey-10 srd:flex srd:items-center srd:justify-between srd:gap-3 srd:rounded-xl srd:px-4 srd:py-3'>
      <div className='srd:flex srd:flex-col srd:gap-1.5'>
        <Skeleton className='srd:h-[18px] srd:w-[140px] srd:rounded-full' />
        <Skeleton className='srd:h-[14px] srd:w-[100px] srd:rounded-full' />
      </div>
      <Skeleton className='srd:h-8 srd:w-[70px] srd:rounded-full' />
    </div>
  )
}

/**
 * Card-style list of the devices currently signed in to the user's account,
 * matching the "Perangkat aktif" section of the account security dialog.
 *
 * The current device is labelled and cannot be revoked from here — signing the
 * current session out is what the app's logout action is for.
 */
export function ActiveDevicesList({ className, locale = 'id' }: ActiveDevicesListProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()
  const authClient = useAuthClient()

  const revokeSessionMutation = useMutation(
    trpc.session.revokeSession.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.session.listAllSessions.queryKey() })
      },
    }),
  )

  const {
    data: activeOrganization,
    isPending: isPendingActiveOrganization,
    error: errorActiveOrganization,
    refetch: refetchActiveOrganization,
  } = authClient.useActiveOrganization()

  const {
    data: currentSession,
    isPending: isPendingCurrentSession,
    error: errorCurrentSession,
    refetch: refetchCurrentSession,
  } = authClient.useSession()

  const {
    data: listAllSessions,
    isPending: isPendingListAllSessions,
    refetch: refetchListAllSessions,
    isError: isErrorListAllSessions,
  } = useQuery({ ...trpc.session.listAllSessions.queryOptions(), staleTime: 1000 * 60 * 5 })

  const isPending = isPendingActiveOrganization || isPendingListAllSessions || isPendingCurrentSession
  const isError = errorActiveOrganization !== null || isErrorListAllSessions || errorCurrentSession

  if (isPending) {
    return (
      <div className={cn('srd:flex srd:flex-col srd:gap-2', className)}>
        <DeviceRowSkeleton />
        <DeviceRowSkeleton />
        <DeviceRowSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={cn('srd:flex srd:flex-col srd:items-start srd:gap-2', className)}>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error srd:flex srd:items-center')}>
          <X className='srd:mr-2 srd:size-4' />
          {locale === 'id'
            ? 'Ada error yang terjadi saat mengambil data perangkat aktif'
            : 'An error occurred while fetching active device data'}
        </p>

        <Button
          variant='outline'
          size='sm'
          onClick={async () => {
            await refetchActiveOrganization()
            await refetchListAllSessions()
            await refetchCurrentSession()
          }}
        >
          <RefreshCcw className='srd:text-blue-100 srd:mr-2 srd:size-4' />
          {locale === 'id' ? 'Coba lagi' : 'Try again'}
        </Button>
      </div>
    )
  }

  const sessions = listAllSessions
    .filter((session) => session.activeOrganizationId === activeOrganization?.id)
    .sort((a, b) => {
      const aIsCurrent = a.id === currentSession?.session.id
      const bIsCurrent = b.id === currentSession?.session.id
      if (aIsCurrent && !bIsCurrent) return -1
      if (!aIsCurrent && bIsCurrent) return 1
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

  if (sessions.length === 0) {
    return (
      <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100', className)}>
        {locale === 'id' ? 'Belum ada perangkat aktif' : 'No active devices yet'}
      </p>
    )
  }

  return (
    <div className={cn('srd:flex srd:flex-col srd:gap-2', className)}>
      {sessions.map((session) => {
        const browser = `${session.browserName}${session.browserVersion ? ` ${session.browserVersion}` : ''}`.trim()
        const deviceName = [DEVICE_LABEL[session.deviceType], browser].filter(Boolean).join(' · ')

        const locationText =
          session.ipAddress && (session.city ?? session.country)
            ? `${session.ipAddress} (${[session.city, session.country].filter(Boolean).join(', ')})`
            : (session.ipAddress ?? '—')

        const isCurrentSession = session.id === currentSession?.session.id
        const isImpersonated = session.impersonatedBy !== null
        const isRevoking = revokeSessionMutation.isPending && revokeSessionMutation.variables.sessionId === session.id

        return (
          <div
            key={session.id}
            className='srd:bg-grey-10 srd:flex srd:items-center srd:justify-between srd:gap-3 srd:rounded-xl srd:px-4 srd:py-3'
          >
            <div className='srd:flex srd:min-w-0 srd:flex-col'>
              <div className='srd:flex srd:min-w-0 srd:items-center srd:gap-2'>
                <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black srd:truncate')}>
                  {deviceName}
                </p>

                {isCurrentSession && (
                  <Badge className='srd:bg-purple-10 srd:text-purple-100 srd:shrink-0'>
                    {locale === 'id' ? 'Perangkat ini' : 'This device'}
                  </Badge>
                )}

                {isImpersonated && <Badge className='srd:shrink-0'>Tim PiBo</Badge>}
              </div>

              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black srd:truncate')}>
                {locationText}
              </p>
            </div>

            {!isCurrentSession && (
              <Button
                variant='ghost'
                size='xs'
                disabled={revokeSessionMutation.isPending}
                onClick={() => revokeSessionMutation.mutate({ sessionId: session.id })}
                className='srd:text-grey-100 srd:hover:text-error srd:shrink-0 srd:rounded-full'
              >
                {isRevoking ? (
                  <Loader2 className='srd:mr-1.5 srd:size-4 srd:animate-spin' />
                ) : (
                  <LogOut className='srd:mr-1.5 srd:size-4' />
                )}
                {locale === 'id' ? 'Keluar' : 'Sign out'}
              </Button>
            )}
          </div>
        )
      })}
    </div>
  )
}
